import type { Page, Pollable, components } from "@theta/api-client";
import {
  MAX_PAGE_LIMIT,
  dataOf,
  pageLimit,
  pageOf,
  pollablePageOf,
  thetaApi,
  unwrapV1,
} from "./theta";

/**
 * DIRECT MESSAGES, ON /api/v1 — spec §2.14 / §4.1
 *
 * The twelve hand-written response interfaces this file used to carry are
 * gone: the types below are `components["schemas"]`, so a field renamed in
 * `backend/src/schemas/dm.ts` fails `pnpm typecheck` here.
 *
 * FIVE LEGACY ENDPOINTS HAVE NO SUCCESSOR, because the contract collapsed them:
 *   unread-count          -> `GET /me` (§1.10 — one document so the header
 *                            does not need three polls).
 *   :id/details           -> folded into `GET /conversations/:id` as
 *                            `sharedGroups` with tenure (§2.14).
 *   with/:username        -> `GET /users/{id}/messaging` (useSendMessageById).
 *   :id/block             -> `PUT /me/blocks/{userId}`: a block is on a PERSON.
 *   recipients            -> `GET /users?messageableBy=me`.
 */

type Schemas = components["schemas"];

export type Conversation = Schemas["Conversation"];
export type ConversationParticipant = Schemas["ConversationParticipant"];
export type Participation = Schemas["Participation"];
export type TenuredGroup = Schemas["TenuredGroup"];
export type Message = Schemas["Message"];
export type MessageReaction = Schemas["MessageReaction"];
export type MessageQuote = Schemas["MessageQuote"];
export type Reactor = Schemas["Reactor"];
export type ReportTargets = Schemas["ReportTargets"];
export type DmReportResult = Schemas["DmReportResult"];
export type Block = Schemas["Block"];
export type UserSummary = Schemas["UserSummary"];
export type Attachment = Schemas["Attachment"];
export type GroupSummary = Schemas["GroupSummary"];
export type CreateMessageInput = Schemas["CreateMessageInput"];
export type { Page, Pollable };

/**
 * The `?limit=` fragment of a query string, clamped to what the route serves.
 *
 * The ceiling itself is `MAX_PAGE_LIMIT` in `lib/api/theta.ts` — one number
 * for the whole app, held to `backend/openapi.json` by
 * `lib/api/__tests__/pageLimits.test.ts`, because OpenAPI's `maximum` is the
 * one part of the contract the generated types cannot carry. What this adds is
 * that a DM caller does not spell a limit into a query string at all: it says
 * what it wants and the clamp happens on the boundary every DM read crosses,
 * so an over-max limit cannot leave this file.
 */
function limitQuery(limit: number | undefined): { limit?: number } {
  return limit === undefined ? {} : { limit: pageLimit(limit) };
}

/**
 * The DM surface has no hand-written methods on `@theta/api-client` (and this
 * batch may not add any — the package is regenerated from the contract, not
 * edited by a screen), so every call below goes through `thetaApi.http`, the
 * client's own "raw typed transport, for a call this client has no method for
 * yet". The path and the query string are still checked at compile time; the
 * envelope and the `ThetaError` come from `lib/api/theta.ts`.
 */

/** A 204 answer: nothing to unwrap, but a non-2xx still has to throw. */
function throwIfFailed(result: { error?: unknown; response: Response }): void {
  if (result.error !== undefined || !result.response.ok) {
    unwrapV1<never>({ ...result, data: undefined });
  }
}

// The inbox

/** The inbox AND the requests, in one cursor-paged, pollable collection. */
export function getConversations(
  init: {
    state?: "accepted" | "pending";
    cursor?: string | null;
    limit?: number;
    ifChanged?: string;
  } = {},
): Promise<Pollable<Page<Conversation>>> {
  return thetaApi.http
    .GET("/conversations", {
      params: {
        query: {
          ...(init.state ? { state: init.state } : {}),
          ...(init.cursor ? { cursor: init.cursor } : {}),
          ...limitQuery(init.limit),
          ...(init.ifChanged ? { ifChanged: init.ifChanged } : {}),
        },
      },
    })
    .then(pollablePageOf<Conversation>);
}

/**
 * One conversation: roster, shared groups WITH TENURE, read state and `can`.
 * Deliberately not pollable — the 1 Hz poll targets `/messages`, and this
 * document only moves when somebody joins, leaves, mutes or accepts.
 * A non-participant gets 404, never 403 (D7).
 */
export async function getConversation(id: number): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.GET("/conversations/{id}", { params: { path: { id } } }),
  );
}

/**
 * Start one. `recipientUserIds`, not usernames (D8): people are id-addressed
 * (O-21), and ten accounts carry a numeric username equal to a DIFFERENT
 * user's id (O-21a), so a handle was never an address.
 *
 * A recipient who has BLOCKED the sender still answers 201, seated `declined` —
 * a refusal would confirm to a spammer that the target is a live account.
 */
export async function startConversation(body: {
  recipientUserIds: Schemas["CreateConversationInput"]["recipientUserIds"];
  bodyMarkdown: string;
}): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.POST("/conversations", { body }),
  );
}

// Messages

/**
 * A STABLE RANGE — not "the newest N".
 *
 * `cursor: prevCursor` pages toward OLDER, `after` toward NEWER, and an
 * unanchored read gets the newest `limit`. Rows are ASCENDING by id in every
 * arm, and `prevCursor` is non-null exactly when older messages exist.
 *
 * This is what deletes the thread's sliding-window bookkeeping: the legacy
 * window moved forward as messages arrived, so a frozen "older" page and the
 * live window could develop an invisible hole between them.
 */
export function getMessages(
  conversationId: number,
  init: {
    before?: number;
    after?: number;
    cursor?: string | null;
    limit?: number;
    ifChanged?: string;
  } = {},
): Promise<Pollable<Page<Message>>> {
  return thetaApi.http
    .GET("/conversations/{id}/messages", {
      params: {
        path: { id: conversationId },
        query: {
          ...(init.before !== undefined ? { before: init.before } : {}),
          ...(init.after !== undefined ? { after: init.after } : {}),
          ...(init.cursor ? { cursor: init.cursor } : {}),
          ...limitQuery(init.limit),
          ...(init.ifChanged ? { ifChanged: init.ifChanged } : {}),
        },
      },
    })
    .then(pollablePageOf<Message>);
}

/**
 * Send one.
 *
 * THE FIRST MESSAGE TO A PENDING CONVERSATION ACCEPTS IT, and the server says
 * so in `meta.sideEffects.conversationAccepted` (§2.10 #87): the caller could
 * not have predicted it, and the inbox has to move the row out of the requests
 * tab without a second read. That flag is why this returns an object rather
 * than the bare `Message`.
 */
export async function sendMessage(
  conversationId: number,
  body: CreateMessageInput,
): Promise<{ message: Message; accepted: boolean }> {
  const envelope = unwrapV1<Message>(
    await thetaApi.http.POST("/conversations/{id}/messages", {
      params: { path: { id: conversationId } },
      body,
    }),
  );
  return {
    message: envelope.data,
    accepted: envelope.meta.sideEffects?.conversationAccepted === true,
  };
}

/**
 * Move the read marker forward; the answer is the new unread count (§2.10 #90).
 * Idempotent by construction (D10b) — the marker only ever moves forward — so
 * there is no `Idempotency-Key` and nothing for a caller to guard against.
 */
export async function markConversationRead(
  conversationId: number,
  throughMessageId?: Message["id"],
): Promise<number> {
  return dataOf<Schemas["ReadState"]>(
    await thetaApi.http.POST("/conversations/{id}/read", {
      params: { path: { id: conversationId } },
      body: throughMessageId === undefined ? {} : { throughMessageId },
    }),
  ).unreadCount;
}

/**
 * Move the read marker BACK, to just before the newest message somebody else
 * wrote (OSO-520) — the only write on this surface that moves it backwards,
 * which is why it is its own route and not a flag on the one above. No body:
 * the unit is the conversation, not a message. The answer is the new unread
 * count, normally 1.
 */
export async function markConversationUnread(
  conversationId: number,
): Promise<number> {
  return dataOf<Schemas["ReadState"]>(
    await thetaApi.http.POST("/conversations/{id}/unread", {
      params: { path: { id: conversationId } },
    }),
  ).unreadCount;
}

/**
 * Your own seat. A MUTED CONVERSATION STILL REPORTS ITS TRUE `unreadCount`
 * here and in the inbox; muting subtracts it from the header badge on
 * `GET /me` and from nothing else. The two numbers answer different questions.
 */
export async function setMuted(
  conversationId: number,
  muted: boolean,
): Promise<Participation> {
  return dataOf<Participation>(
    await thetaApi.http.PATCH("/conversations/{id}/participation", {
      params: { path: { id: conversationId } },
      body: { muted },
    }),
  );
}

/**
 * Give a group conversation a name, or clear it with `null`.
 *
 * THE ONLY WRITABLE FIELD ON A CONVERSATION, and the only title on this
 * surface that may be rendered — the private-message topic this resource
 * replaced carried a generated UUID, which is why a thread is otherwise
 * labelled with its participants. That fallback stays: `null` puts it back,
 * and a name that is only whitespace is stored as `null` rather than as `""`.
 *
 * Group threads only, and then any live seat — `can.rename` is that rule,
 * decided by the server. Never re-derive it from `participants.length`.
 */
export async function renameConversation(
  id: number,
  name: string | null,
): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.PATCH("/conversations/{id}", {
      params: { path: { id } },
      body: { name },
    }),
  );
}

/** Add people. Creator only — `can.addParticipants` is that rule, decided. */
export async function addParticipants(
  conversationId: number,
  userIds: Schemas["AddParticipantsInput"]["userIds"],
): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.POST("/conversations/{id}/participants", {
      params: { path: { id: conversationId } },
      body: { userIds },
    }),
  );
}

/** Leave. POST, not DELETE: the seat stays (quirk D11), so history stays attributable. */
export async function leaveConversation(id: number): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.POST("/conversations/{id}/leave", { params: { path: { id } } }),
  );
}

/** Accept. Idempotent; answers the conversation with `can` recomputed. */
export async function acceptRequest(id: number): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.POST("/conversations/{id}/accept", { params: { path: { id } } }),
  );
}

/**
 * Decline. NOTHING IS SENT TO THE SENDER — a decline receipt is a probe a
 * spammer could use to find live accounts. It does not remove the seat and
 * does not block, so a bookmarked URL keeps working; the thread simply leaves
 * the inbox and goes silent.
 */
export async function declineRequest(id: number): Promise<Conversation> {
  return dataOf<Conversation>(
    await thetaApi.http.POST("/conversations/{id}/decline", { params: { path: { id } } }),
  );
}

/**
 * Withdraw one of your own messages — OSO-564.
 *
 * The only write there is on a message that is not a reaction: a message
 * cannot be edited while a failed video transcode is holding it back, so this
 * stays the whole recovery story for that state (such a message is never
 * delivered, and its `media_failed` notification points its own sender at it).
 * Soft on the server; the thread's 1 Hz poll drops the row on its own, because
 * a deletion moves the token the poll is conditional on.
 *
 * Addressed by the message id alone — the conversation is derivable from it,
 * the same reason the reaction routes carry no conversation (D10b).
 */
export async function deleteMessage(messageId: number): Promise<void> {
  throwIfFailed(
    await thetaApi.http.DELETE("/messages/{id}", {
      params: { path: { id: messageId } },
    }),
  );
}

/**
 * REWRITE ONE MESSAGE'S WORDS — OSO-607. The answer is the whole message, with
 * `editedAt` stamped, so the caller splices it rather than recomposing one.
 *
 * The TEXT and nothing else: the attachments on a message are what was
 * actually sent, and the route takes no list for them.
 *
 * NO `Idempotency-Key`. A PATCH that sets the body TO a value is idempotent by
 * construction — a replay writes the same words a second time — which is the
 * same reason `POST /conversations/{id}/read` carries none.
 */
export async function editMessage(
  messageId: number,
  bodyMarkdown: string,
): Promise<Message> {
  return dataOf<Message>(
    await thetaApi.http.PATCH("/messages/{id}", {
      params: { path: { id: messageId } },
      body: { bodyMarkdown },
    }),
  );
}

// Reactions

/**
 * React, keyed by emoji in the path (D10b): a DM reaction is N per user,
 * unlike a post reaction, which is a singleton. AN UPSERT, NEVER A TOGGLE, so
 * a retried request cannot remove the reaction it asked for — the caller picks
 * between this and `removeMessageReaction` from the `mine` flag the server
 * already put on the pill.
 *
 * The answer is the complete, authoritative pill list. Splice it.
 */
export async function addMessageReaction(
  messageId: number,
  emoji: string,
): Promise<MessageReaction[]> {
  return dataOf<Schemas["MessageReactions"]>(
    await thetaApi.http.PUT("/messages/{id}/reactions/{emoji}", {
      params: { path: { id: messageId, emoji } },
    }),
  ).reactions;
}

/** Remove one. 200 with the summary even when there was nothing to remove (QR8). */
export async function removeMessageReaction(
  messageId: number,
  emoji: string,
): Promise<MessageReaction[]> {
  return dataOf<Schemas["MessageReactions"]>(
    await thetaApi.http.DELETE("/messages/{id}/reactions/{emoji}", {
      params: { path: { id: messageId, emoji } },
    }),
  ).reactions;
}

/**
 * Who reacted, and with what — the SAME `Reactor` shape as
 * `GET /posts/{id}/reactions`, so one renderer serves both surfaces.
 */
export async function getMessageReactors(
  messageId: number,
  init: { cursor?: string | null; limit?: number } = {},
): Promise<Page<Reactor>> {
  return pageOf<Reactor>(
      await thetaApi.http.GET("/messages/{id}/reactions", {
        params: {
          path: { id: messageId },
          query: {
            ...(init.cursor ? { cursor: init.cursor } : {}),
            ...limitQuery(init.limit),
          },
        },
      }),
  );
}

// Reporting and blocking

/**
 * Who can be reported from here, and to which shared groups.
 *
 * AN EMPTY `groups` MEANS THE REPORT CANNOT BE FILED AT ALL (§2.10 #96) — a
 * refusal the dialog has to render, not a "pick one later" state. `can.report`
 * carries all three of the report route's refusals (a shared group, no
 * duplicate, and the evidence rule).
 */
export async function getReportTargets(
  conversationId: number,
  userId?: number,
): Promise<ReportTargets> {
  return dataOf<ReportTargets>(
    await thetaApi.http.GET("/conversations/{id}/report-targets", {
      params: {
        path: { id: conversationId },
        query: userId === undefined ? {} : { userId },
      },
    }),
  );
}

/**
 * File it. `{block: true}` also blocks the person — the standalone block route
 * is MERGED into this one, so two paths to one outcome cannot leave different
 * state.
 */
export async function reportSender(
  conversationId: number,
  body: Schemas["CreateDmReportInput"],
): Promise<DmReportResult> {
  return dataOf<DmReportResult>(
    await thetaApi.http.POST("/conversations/{id}/reports", {
      params: { path: { id: conversationId } },
      body,
    }),
  );
}

/** The people I have blocked. Cursor-paged (D4); the old endpoint returned every row. */
export async function getBlocks(
  init: { cursor?: string | null; limit?: number } = {},
): Promise<Page<Block>> {
  return pageOf<Block>(
      await thetaApi.http.GET("/me/blocks", {
        params: {
          query: {
            ...(init.cursor ? { cursor: init.cursor } : {}),
            ...limitQuery(init.limit),
          },
        },
      }),
  );
}

/**
 * Block a person. Idempotent by construction. SIDE EFFECT, and it is the
 * point: blocking DECLINES every live 1:1 thread with them, exactly as
 * reporting with `{block: true}` does.
 */
export async function blockUser(userId: number): Promise<Block> {
  return dataOf<Block>(
    await thetaApi.http.PUT("/me/blocks/{userId}", { params: { path: { userId } } }),
  );
}

/** Lift a block. 204, and it succeeds when there was nothing to lift. */
export async function unblockUser(userId: number): Promise<void> {
  throwIfFailed(
    await thetaApi.http.DELETE("/me/blocks/{userId}", { params: { path: { userId } } }),
  );
}

/**
 * People I may write to. SCOPE IS REQUIRED (§1.4), and `messageableBy: "me"`
 * IS the DM policy — so the picker can no longer offer someone the send would
 * then refuse. `hasMore: false` is the "complete, filter locally" signal that
 * replaced the old bespoke `complete` flag.
 *
 * THE LIMIT IS THE ROUTE'S MAXIMUM, not the route's default. The picker does
 * not page — a dropdown under a search box is a "type more" affordance, not a
 * scroller — so the page it asks for is the whole answer it will ever have,
 * and on the default 20 it silently hid every match past the twentieth while
 * `excludeIds` filtered that same truncated page down further. `hasMore` is
 * returned so the caller can SAY the list was cut rather than look complete.
 */
export async function searchRecipients(term: string): Promise<Page<UserSummary>> {
  return pageOf<UserSummary>(
      await thetaApi.http.GET("/users", {
        params: {
          query: { q: term, messageableBy: "me", ...limitQuery(MAX_PAGE_LIMIT) },
        },
      }),
  );
}
